import { cn } from 'component-library';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DOMAIN_FACT_TYPES, IndicatorFactType, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { WidgetSlot, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { useBuildPathWithDefaultQueryParams } from '@/hooks/useBuildPathWithDefaultQueryParams';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePathSet } from '@/hooks/usePathSet';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { isNullish } from '@/utils/booleans';
import { formatFactKilogramsToTonnes } from '@/utils/formatting/formatKgToTon';

import { CapsuleTrend } from '../Capsule/Capsule';
import { NCFactCapsule } from '../NCFactCapsule/NCFactCapsule';
import { getIndicatorCopy, IndicatorType } from './utils';

const useIndicatorHealthConfiguration = ({
  facts,
  latestFactType,
  trendFactType,
  routeSlug,
  indicator,
  visible = true,
}: {
  facts: Record<string, PlotReportFactElement<unknown> | undefined>;
  latestFactType: IndicatorFactType;
  trendFactType: IndicatorFactType;
  routeSlug: string;
  indicator: IndicatorType;
  visible: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const latestFact = formatFactKilogramsToTonnes(facts[`${latestFactType}`]);
  const upliftFact = formatFactKilogramsToTonnes(facts[`${trendFactType}`]);

  const upliftFactDisplay = `${useDisplayNumber((upliftFact?.value as string) ?? 0, { signDisplay: 'always' })} %`;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const indicatorCopy = getIndicatorCopy(indicator, latestFact?.value as number);

  return {
    name: 'default_ecosystem_health_overview',
    type: WidgetType.o_card,
    visible: !isNullish(latestFact?.value) && visible,
    relatedData: {
      styles: {
        root: 'w-full rounded-2xl hover:cursor-pointer hover:bg-neutral-black-4 hover:rounded-2xl',
        header: 'border-none',
        body: 'w-full border-none px-3 py-2',
        footer: 'px-0 py-0',
      },
    },
    widgets: [
      {
        name: `default_ecosystem_health_overview_${latestFactType}`,
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          className: 'w-full p-2 justify-center',
          onClick: () => navigate(routeSlug),
        },
        widgets: [
          {
            name: `domain_stats_${latestFactType}`,
            type: WidgetType.a_stack,
            visible: true,
            relatedData: {
              className: 'w-full flex-col gap-2 items-start justify-start',
            },
            widgets: [
              {
                name: `domain_${latestFactType}`,
                type: WidgetType.a_stack,
                visible: true,
                relatedData: {
                  className: 'w-full flex-col gap-2 items-start justify-start',
                },
                widgets: [
                  {
                    name: `domain_name_${latestFactType}`,
                    type: WidgetType.a_text,
                    visible: true,
                    relatedData: {
                      text: t(`ecosystemHealth.domain.${latestFactType as keyof typeof DOMAIN_FACT_TYPES}.title`),
                      className: 'w-full typography-overline text-text-secondary',
                    },
                  },
                  {
                    name: `domain_benchmark_${latestFactType}`,
                    type: WidgetType.o_card,
                    visible: true,
                    relatedData: {
                      styles: {
                        root: 'w-full border-none flex flex-row justify-start items-start bg-transparent',
                        body: 'w-full px-0 py-1',
                      },
                      body: (
                        <NCFactCapsule
                          className='w-full justify-between whitespace-nowrap'
                          label=''
                          fact={latestFact}
                          trend={
                            typeof upliftFact?.value === 'number' ? (
                              <CapsuleTrend
                                className='whitespace-nowrap'
                                variant={upliftFact.value < 0 ? 'negative' : 'positive'}
                              >
                                {upliftFactDisplay}
                              </CapsuleTrend>
                            ) : (
                              <span>-</span>
                            )
                          }
                        />
                      ),
                    },
                  },
                ],
              },
            ],
          },
          // {
          //   name: `domain_explainer_${latestFactType}`,
          //   type: WidgetType.a_text,
          //   visible: !isNullish(indicatorCopy),
          //   relatedData: {
          //     text: indicatorCopy ?? '',
          //     className: 'typography-body2 text-text-secondary',
          //   },
          // },
        ],
      },
    ],
  } as WidgetSlot;
};

type AllIndicatorsHealthProps = {
  styles?: Record<string, string>;
  domainVisibility?: {
    carbon: boolean;
    water: boolean;
    biodiversity: boolean;
    soil: boolean;
    social: boolean;
  };
};
export const useAllIndicatorsHealthConfiguration = (props?: AllIndicatorsHealthProps) => {
  const pathSet = usePathSet();

  const facts = useOrganizationIndicatorReportingFact(Object.values(DOMAIN_FACT_TYPES) as IndicatorFactType[]);

  const { buildPathWithDefaultQueryParams } = useBuildPathWithDefaultQueryParams();

  const carbonHealthWidget = useIndicatorHealthConfiguration({
    facts,
    latestFactType: DOMAIN_FACT_TYPES.carbon_combined_per_ha,
    trendFactType: DOMAIN_FACT_TYPES.carbon_combined_previous_1y_pct_change,
    routeSlug: buildPathWithDefaultQueryParams(pathSet.carbonDetails),
    indicator: 'carbon',
    visible: props?.domainVisibility?.carbon ?? true,
  });
  const waterHealthWidget = useIndicatorHealthConfiguration({
    facts,
    latestFactType: DOMAIN_FACT_TYPES.water_fc_per_ha,
    trendFactType: DOMAIN_FACT_TYPES.water_fc_previous_1y_uplift_per_ha_pct_change,
    routeSlug: buildPathWithDefaultQueryParams(pathSet.waterDetails),
    indicator: 'water',
    visible: props?.domainVisibility?.water ?? true,
  });
  const biodiversityHealthWidget = useIndicatorHealthConfiguration({
    facts,
    latestFactType: DOMAIN_FACT_TYPES.biodiversity_buffer_zone_pct,
    trendFactType: DOMAIN_FACT_TYPES.biodiversity_buffer_zone_pct_previous_1y_uplift_pct_change,
    routeSlug: buildPathWithDefaultQueryParams(pathSet.biodiversityDetails),
    indicator: 'biodiversity',
    visible: props?.domainVisibility?.biodiversity ?? true,
  });
  const soilHealthWidget = useIndicatorHealthConfiguration({
    facts,
    latestFactType: DOMAIN_FACT_TYPES.permanent_soil_carbon_per_ha,
    trendFactType: DOMAIN_FACT_TYPES.permanent_soil_carbon_previous_1y_uplift_per_ha_pct_change,
    routeSlug: buildPathWithDefaultQueryParams(pathSet.soilDetails),
    indicator: 'soil',
    visible: props?.domainVisibility?.soil ?? true,
  });
  const socialHealthWidget = useIndicatorHealthConfiguration({
    facts,
    latestFactType: DOMAIN_FACT_TYPES.social_life_expectancy,
    trendFactType: DOMAIN_FACT_TYPES.social_life_expectancy_previous_1y_pct_change,
    routeSlug: buildPathWithDefaultQueryParams(pathSet.socialDetails),
    indicator: 'social',
    visible: props?.domainVisibility?.social ?? true,
  });

  return {
    name: 'default_ecosystem_health_overview',
    type: WidgetType.a_stack,
    visible: true,
    relatedData: {
      className: cn('grid grid-cols-5 gap-4 w-full', props?.styles?.root),
    },
    widgets: [carbonHealthWidget, waterHealthWidget, biodiversityHealthWidget, soilHealthWidget, socialHealthWidget],
  } as WidgetSlotConfiguration;
};
